import { Routes } from '@angular/router';
import { authenticatedCanActivateGuard } from './guards/authenticated.can-activate.guard';
import { unauthenticatedCanActivateGuard } from './guards/unauthenticated.can-activate.guard';
import { phoneVerificationCanActivateGuard } from './guards/phone-verification.can-activate.guard';
import { emailLinkVerificationCanActivateGuard } from './guards/email-link-verification.can-activate.guard';
import { adminOnlyCanActivateGuard } from './guards/admin-only.can-activate.guard';
import { superAdminOnlyCanActivateGuard } from './guards/super-admin-only.can-activate.guard';
import { unsavedChangesGuard } from './guards/unsaved-changes.guard';
import { worklistSubmissionCanDeactivateGuard } from './guards/worklist-submission-can-deactivate.guard';
import { worklistResolver } from './resolvers/worklist.resolver';
import { worklistSubmissionAssignmentResolver } from './resolvers/worklist-submission-assignment.resolver';

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'splash',
    pathMatch: 'full',
  },
  {
    path: 'splash',
    loadComponent: () => import('./pages/splash/splash.page').then((m) => m.SplashPage),
    canActivate: [unauthenticatedCanActivateGuard],
  },
  {
    path: 'email-link-sign-in',
    loadComponent: () => import('./pages/email-link-sign-in/email-link-sign-in.page').then((m) => m.EmailLinkSignInPage),
    canActivate: [unauthenticatedCanActivateGuard],
  },
  {
    path: 'verify-email-link',
    loadComponent: () => import('./pages/verify-email-link/verify-email-link.page').then((m) => m.VerifyEmailLinkPage),
    canActivate: [unauthenticatedCanActivateGuard, emailLinkVerificationCanActivateGuard],
  },
  {
    path: 'phone-sign-in',
    loadComponent: () => import('./pages/phone-sign-in/phone-sign-in.page').then((m) => m.PhoneSignInPage),
    canActivate: [unauthenticatedCanActivateGuard],
  },
  {
    path: 'verify-phone-code',
    loadComponent: () => import('./pages/verify-phone-code/verify-phone-code.page').then((m) => m.VerifyPhoneCodePage),
    canActivate: [unauthenticatedCanActivateGuard, phoneVerificationCanActivateGuard],
  },
  {
    path: 'console',
    loadComponent: () => import('./pages/console/console.page').then((m) => m.ConsolePage),
    canActivate: [authenticatedCanActivateGuard],
    canActivateChild: [authenticatedCanActivateGuard],
    children: [
      {
        path: '',
        redirectTo: 'home',
        pathMatch: 'full',
      },
      {
        path: 'playground',
        loadComponent: () => import('./pages/playground/playground.page').then((m) => m.PlaygroundPage),
      },
      {
        path: 'home',
        loadComponent: () => import('./pages/home/home.page').then((m) => m.HomePage),
      },
      {
        path: 'account',
        loadComponent: () => import('./pages/account/account.page').then((m) => m.AccountPage),
        canDeactivate: [unsavedChangesGuard],
      },
      {
        path: 'courses',
        loadComponent: () => import('./pages/course-list/course-list.page').then((m) => m.CourseListPage),
      },
      {
        path: 'course/:courseId',
        loadComponent: () => import('./pages/course/course.page').then((m) => m.CoursePage),
      },
      {
        path: 'course/:courseId/assignments',
        loadComponent: () => import('./pages/assignment-list/assignment-list.page').then((m) => m.AssignmentListPage),
      },
      {
        path: 'course/:courseId/assignment/:assignmentId',
        loadComponent: () => import('./pages/assignment-detail/assignment-detail.page').then((m) => m.AssignmentDetailPage),
      },
      {
        path: 'course/:courseId/assignment/:assignmentId/submissions',
        loadComponent: () => import('./pages/submission-list/submission-list.page').then((m) => m.SubmissionListPage),
      },
      {
        path: 'course/:courseId/assignment/:assignmentId/submission/:canvasUserId',
        loadComponent: () => import('./pages/submission-detail/submission-detail.page').then((m) => m.SubmissionDetailPage),
      },
      {
        path: 'tasks',
        loadComponent: () => import('./pages/task-list/task-list.page').then((m) => m.TaskListPage),
      },
      // Grader
      {
        path: 'practical-assessment-1',
        loadComponent: () => import('./pages/practical-assessment-1/practical-assessment-1.page').then((m) => m.PracticalAssessment1Page),
      },
      {
        path: 'practical-assessment-2',
        loadComponent: () => import('./pages/practical-assessment-2/practical-assessment-2.page').then((m) => m.PracticalAssessment2Page),
      },
      {
        path: 'e32313-assignment-1',
        loadComponent: () => import('./pages/e32313-assignment-1/e32313-assignment-1.page').then((m) => m.E32313Assignment1Page),
      },
      {
        path: 'math2270-assignment-1',
        loadComponent: () => import('./pages/math2270-assessment-1/math2270-assessment-1.page').then((m) => m.Math2270Assessment1Page),
      },
      {
        path: 's1-2026-math2270-assignment-1',
        loadComponent: () => import('./pages/math2270-assessment-1-s1-2026/math2270-assessment-1-s1-2026.page').then((m) => m.Math2270Assessment1S12026Page),
      },
      {
        path: 's1-2026-math2270-assignment-2',
        loadComponent: () => import('./pages/math2270-assessment-2-s1-2026/math2270-assessment-2-s1-2026.page').then((m) => m.Math2270Assessment2S12026Page),
      },
      {
        path: 's1-2026-math2270-assignment-3',
        loadComponent: () => import('./pages/math2270-assessment-3-s1-2026/math2270-assessment-3-s1-2026.page').then((m) => m.Math2270Assessment3S12026Page),
      },
      {
        path: 's1-2026-math2270-assignment-3-test',
        loadComponent: () =>
          import('./pages/math2270-assessment-3-s1-2026-test/math2270-assessment-3-s1-2026-test.page').then((m) => m.Math2270Assessment3S12026TestPage),
      },
      {
        path: 's1-2026-math2349-practical-assessment-1',
        loadComponent: () => import('./pages/math2349-practical-assessment-1-s1-2026/math2349-practical-assessment-1-s1-2026.page').then((m) => m.Math2349PracticalAssessment1S12026Page),
      },
      {
        path: 's1-2026-math2349-practical-assessment-2',
        loadComponent: () => import('./pages/math2349-practical-assessment-2-s1-2026/math2349-practical-assessment-2-s1-2026.page').then((m) => m.Math2349PracticalAssessment2S12026Page),
      },
      {
        path: 's1-2026-math2349-practical-assessment-2-test',
        loadComponent: () =>
          import('./pages/math2349-practical-assessment-2-s1-2026-test/math2349-practical-assessment-2-s1-2026-test.page').then((m) => m.Math2349PracticalAssessment2S12026TestPage),
      },
      {
        path: 's2-2026-math2349-practical-assessment-1',
        loadComponent: () => import('./pages/math2349-practical-assessment-1-s2-2026/math2349-practical-assessment-1-s2-2026.page').then((m) => m.Math2349PracticalAssessment1S22026Page),
      },
      {
        path: 's2-2026-math2349-practical-assessment-1-test',
        loadComponent: () =>
          import('./pages/math2349-practical-assessment-1-s2-2026-test/math2349-practical-assessment-1-s2-2026-test.page').then((m) => m.Math2349PracticalAssessment1S22026TestPage),
      },
      {
        path: 's2-2026-math2349-practical-assessment-2',
        loadComponent: () => import('./pages/math2349-practical-assessment-2-s2-2026/math2349-practical-assessment-2-s2-2026.page').then((m) => m.Math2349PracticalAssessment2S22026Page),
      },
      {
        path: 's2-2026-math2349-practical-assessment-2-test',
        loadComponent: () =>
          import('./pages/math2349-practical-assessment-2-s2-2026-test/math2349-practical-assessment-2-s2-2026-test.page').then((m) => m.Math2349PracticalAssessment2S22026TestPage),
      },
      {
        path: 'math2270-assignment-2',
        loadComponent: () => import('./pages/math2270-assessment-2/math2270-assessment-2.page').then((m) => m.Math2270Assessment2Page),
      },
      {
        path: 'math2270-assignment-3',
        loadComponent: () => import('./pages/math2270-assignment-3/math2270-assignment-3.page').then((m) => m.Math2270Assignment3Page),
      },
      {
        path: 'math2204-final-assessment',
        loadComponent: () => import('./pages/math2204-final-assessment/math2204-final-assessment.page').then((m) => m.Math2204FinalAssessmentPage),
      },
      {
        path: 'rubrics',
        loadComponent: () => import('./pages/rubrics/rubrics.page').then((m) => m.RubricsPage),
      },
      {
        path: 'update-rubric/:rubricId',
        loadComponent: () => import('./pages/update-rubric/update-rubric.page').then((m) => m.UpdateRubricPage),
      },
      {
        path: 'users',
        loadComponent: () => import('./pages/users/users.page').then((m) => m.UsersPage),
        canActivate: [adminOnlyCanActivateGuard],
      },
      {
        path: 'tenants',
        loadComponent: () => import('./pages/tenants/tenants.page').then((m) => m.TenantsPage),
        canActivate: [superAdminOnlyCanActivateGuard],
      },
      {
        path: 'tenants/create',
        loadComponent: () => import('./pages/tenants/create-tenant/create-tenant.page').then((m) => m.CreateTenantPage),
        canActivate: [superAdminOnlyCanActivateGuard],
      },
      {
        path: 'tenants/:tenantId',
        loadComponent: () => import('./pages/tenants/manage-tenant/manage-tenant.page').then((m) => m.ManageTenantPage),
        canActivate: [superAdminOnlyCanActivateGuard],
      },
      {
        path: 'tenants/:tenantId/invite-user',
        loadComponent: () => import('./pages/tenants/invite-user/invite-user.page').then((m) => m.InviteUserPage),
        canActivate: [superAdminOnlyCanActivateGuard],
      },
      {
        path: 'tenants/:tenantId/authentication',
        loadComponent: () => import('./pages/tenants/tenant-authentication/tenant-authentication.page').then((m) => m.TenantAuthenticationPage),
        canActivate: [superAdminOnlyCanActivateGuard],
      },
      {
        path: 'tenants/:tenantId/authentication/sms-mfa',
        loadComponent: () => import('./pages/tenants/tenant-sms-mfa/tenant-sms-mfa.page').then((m) => m.TenantSmsMfaPage),
        canActivate: [superAdminOnlyCanActivateGuard],
      },
      {
        path: 'tenants/:tenantId/authentication/totp-mfa',
        loadComponent: () => import('./pages/tenants/tenant-totp-mfa/tenant-totp-mfa.page').then((m) => m.TenantTotpMfaPage),
        canActivate: [superAdminOnlyCanActivateGuard],
      },
      {
        path: 'tenants/:tenantId/authentication/users',
        loadComponent: () => import('./pages/tenants/manage-users/manage-users.page').then((m) => m.ManageUsersPage),
        canActivate: [superAdminOnlyCanActivateGuard],
      },
      {
        path: 'tenants/:tenantId/authentication/users/add',
        loadComponent: () => import('./pages/tenants/add-user/add-user.page').then((m) => m.AddUserPage),
        canActivate: [superAdminOnlyCanActivateGuard],
      },
      {
        path: 'tenants/:tenantId/authentication/users/:userId',
        loadComponent: () => import('./pages/tenants/manage-user/manage-user.page').then((m) => m.ManageUserPage),
        canActivate: [superAdminOnlyCanActivateGuard],
      },
      {
        path: 'tenants/:tenantId/authentication/users/:userId/authentication',
        loadComponent: () => import('./pages/tenants/manage-authentication/manage-authentication.page').then((m) => m.ManageAuthenticationPage),
        canActivate: [superAdminOnlyCanActivateGuard],
      },
      {
        path: 'tenants/:tenantId/authentication/users/:userId/authentication/phone-sign-in',
        loadComponent: () => import('./pages/tenants/manage-phone-sign-in/manage-phone-sign-in.page').then((m) => m.ManagePhoneSignInPage),
        canActivate: [superAdminOnlyCanActivateGuard],
      },
      {
        path: 'tenants/:tenantId/authentication/users/:userId/authentication/email-link-sign-in',
        loadComponent: () => import('./pages/tenants/manage-email-link-sign-in/manage-email-link-sign-in.page').then((m) => m.ManageEmailLinkSignInPage),
        canActivate: [superAdminOnlyCanActivateGuard],
      },
      {
        path: 'tenants/:tenantId/authentication/users/:userId/authentication/sms-mfa',
        loadComponent: () => import('./pages/tenants/manage-sms-mfa/manage-sms-mfa.page').then((m) => m.ManageSmsMfaPage),
        canActivate: [superAdminOnlyCanActivateGuard],
      },
      {
        path: 'tenants/:tenantId/authentication/users/:userId/authentication/totp-mfa',
        loadComponent: () => import('./pages/tenants/manage-totp-mfa/manage-totp-mfa.page').then((m) => m.ManageTotpMfaPage),
        canActivate: [superAdminOnlyCanActivateGuard],
      },
      {
        path: 'tenants/:tenantId/billing',
        loadComponent: () => import('./pages/tenants/manage-billing/manage-billing.page').then((m) => m.ManageBillingPage),
        canActivate: [superAdminOnlyCanActivateGuard],
      },
      {
        path: 'tenants/:tenantId/rate-limits',
        loadComponent: () => import('./pages/tenants/tenant-rate-limits/tenant-rate-limits.page').then((m) => m.TenantRateLimitsPage),
        canActivate: [superAdminOnlyCanActivateGuard],
      },
      {
        path: 'rate-limits',
        loadComponent: () => import('./pages/rate-limits/rate-limits.page').then((m) => m.RateLimitsPage),
        canActivate: [superAdminOnlyCanActivateGuard],
      },
      {
        path: 'jobs',
        loadComponent: () => import('./pages/serp/manage-jobs/manage-jobs.page').then((m) => m.ManageJobsPage),
      },
      {
        path: 'jobs/create',
        loadComponent: () => import('./pages/serp/create-job/create-job.page').then((m) => m.CreateJobPage),
      },
      {
        path: 'job/:jobId',
        loadComponent: () => import('./pages/serp/manage-job/manage-job.page').then((m) => m.ManageJobPage),
      },
      {
        path: 'job-runs',
        loadComponent: () => import('./pages/serp/manage-job-runs/manage-job-runs.page').then((m) => m.ManageJobRunsPage),
      },
      {
        path: 'job-run/:jobRunId',
        loadComponent: () => import('./pages/serp/manage-job-run/manage-job-run.page').then((m) => m.ManageJobRunPage),
      },
      {
        path: 'job-run/:jobRunId/query-runs',
        loadComponent: () => import('./pages/serp/manage-query-runs/manage-query-runs.page').then((m) => m.ManageQueryRunsPage),
      },
      {
        path: 'job-run/:jobRunId/query-run/:queryRunId/results',
        loadComponent: () => import('./pages/serp/manage-query-run-results/manage-query-run-results.page').then((m) => m.ManageQueryRunResultsPage),
      },
      {
        path: 'job-run/:jobRunId/query-run/:queryRunId/validate',
        loadComponent: () => import('./pages/serp/validate-query-run/validate-query-run.page').then((m) => m.ValidateQueryRunPage),
      },
      {
        path: 'allocations',
        redirectTo: 'allocations/select-course',
      },
      {
        path: 'allocations/select-course',
        loadComponent: () => import('./pages/allocations/select-course/select-course.page').then((m) => m.AllocationSelectCoursePage),
        canActivate: [adminOnlyCanActivateGuard],
      },
      {
        path: 'allocations/course/:courseId/select-assignment',
        loadComponent: () => import('./pages/allocations/select-assignment/select-assignment.page').then((m) => m.SelectAssignmentPage),
        canActivate: [adminOnlyCanActivateGuard],
      },
      {
        path: 'allocations/course/:courseId/assignment/:assignmentId/assignment-overview',
        loadComponent: () => import('./pages/allocations/assignment-overview/assignment-overview.page').then((m) => m.AssignmentOverviewPage),
        canActivate: [adminOnlyCanActivateGuard],
      },
      {
        path: 'allocations/course/:courseId/assignment/:assignmentId/allocation-overview',
        loadComponent: () => import('./pages/allocations/allocation-overview/allocation-overview.page').then((m) => m.AllocationOverviewPage),
        canActivate: [adminOnlyCanActivateGuard],
      },
      {
        path: 'allocations/course/:courseId/assignment/:assignmentId/augmented-intelligence-overview',
        loadComponent: () => import('./pages/allocations/augmented-intelligence-overview/augmented-intelligence-overview.page').then((m) => m.AugmentedIntelligenceOverviewPage),
        canActivate: [adminOnlyCanActivateGuard],
      },
      {
        path: 'allocations/course/:courseId/assignment/:assignmentId/feedback-template-tool',
        loadComponent: () => import('./pages/allocations/feedback-template-tool/feedback-template-tool.page').then((m) => m.FeedbackTemplateToolPage),
        canActivate: [adminOnlyCanActivateGuard],
      },
      {
        path: 'allocations/course/:courseId/assignment/:assignmentId/audio-assets',
        loadComponent: () => import('./pages/allocations/audio-assets/audio-assets.page').then((m) => m.AudioAssetsPage),
        canActivate: [adminOnlyCanActivateGuard],
      },
      {
        path: 'allocations/course/:courseId/assignment/:assignmentId/audio-assets/create',
        loadComponent: () => import('./pages/allocations/create-audio-asset/create-audio-asset.page').then((m) => m.CreateAudioAssetPage),
        canActivate: [adminOnlyCanActivateGuard],
      },
      {
        path: 'allocations/course/:courseId/assignment/:assignmentId/feedback-templates',
        loadComponent: () => import('./pages/allocations/feedback-templates/feedback-templates.page').then((m) => m.FeedbackTemplatesPage),
        canActivate: [adminOnlyCanActivateGuard],
      },
      {
        path: 'allocations/course/:courseId/assignment/:assignmentId/feedback-templates/create',
        loadComponent: () => import('./pages/allocations/create-feedback-template/create-feedback-template.page').then((m) => m.CreateFeedbackTemplatePage),
        canActivate: [adminOnlyCanActivateGuard],
      },
      {
        path: 'allocations/course/:courseId/assignment/:assignmentId/feedback-templates/edit/:templateId',
        loadComponent: () => import('./pages/allocations/create-feedback-template/create-feedback-template.page').then((m) => m.CreateFeedbackTemplatePage),
        canActivate: [adminOnlyCanActivateGuard],
      },
      {
        path: 'allocations/course/:courseId/assignment/:assignmentId/ai-knowledge-base',
        loadComponent: () => import('./pages/allocations/ai-knowledge-base/ai-knowledge-base.page').then((m) => m.AIKnowledgeBasePage),
        canActivate: [adminOnlyCanActivateGuard],
      },
      {
        path: 'allocations/course/:courseId/assignment/:assignmentId/manage-ai-agents',
        loadComponent: () => import('./pages/allocations/manage-ai-agents/manage-ai-agents.page').then((m) => m.ManageAIAgentsPage),
        canActivate: [adminOnlyCanActivateGuard],
      },
      {
        path: 'allocations/course/:courseId/assignment/:assignmentId/manage-ai-agents/create',
        loadComponent: () => import('./pages/allocations/create-agent/create-agent.page').then((m) => m.CreateAgentPage),
        canActivate: [adminOnlyCanActivateGuard],
      },
      {
        path: 'allocations/course/:courseId/assignment/:assignmentId/manage-ai-agents/edit/:agentId',
        loadComponent: () => import('./pages/allocations/create-agent/create-agent.page').then((m) => m.CreateAgentPage),
        canActivate: [adminOnlyCanActivateGuard],
      },
      {
        path: 'allocations/course/:courseId/assignment/:assignmentId/manage-trays',
        loadComponent: () => import('./pages/allocations/manage-trays/manage-trays.page').then((m) => m.ManageTraysPage),
        canActivate: [adminOnlyCanActivateGuard],
      },
      {
        path: 'allocations/course/:courseId/assignment/:assignmentId/tray/:trayId',
        loadComponent: () => import('./pages/allocations/manage-tray/manage-tray.page').then((m) => m.ManageTrayPage),
        canActivate: [adminOnlyCanActivateGuard],
      },
      {
        path: 'worklists/course/:courseId/assignment/:assignmentId',
        loadComponent: () => import('./pages/worklist/worklist.page').then((m) => m.WorklistPage),
        resolve: { worklist: worklistResolver },
      },
      {
        path: 'worklists/course/:courseId/assignment/:assignmentId/submission/:submissionId',
        loadComponent: () => import('./pages/worklist-submission/worklist-submission.page').then((m) => m.WorklistSubmissionPage),
        canActivate: [authenticatedCanActivateGuard],
        canDeactivate: [worklistSubmissionCanDeactivateGuard],
        resolve: { assignmentName: worklistSubmissionAssignmentResolver },
      },
    ],
  },
  {
    path: 'unavailable',
    loadComponent: () => import('./pages/unavailable/unavailable.page').then((m) => m.UnavailablePage),
  },
  {
    path: 'unauthorised',
    loadComponent: () => import('./pages/unauthorised/unauthorised.page').then((m) => m.UnauthorisedPage),
  },
  {
    path: 'account-disabled',
    loadComponent: () => import('./pages/account-disabled/account-disabled.page').then((m) => m.AccountDisabledPage),
  },
  {
    path: '**',
    redirectTo: 'console',
    pathMatch: 'full',
  },
];
